import React from 'react';
import { Tag } from 'antd';

// Shapes and formatters for worker telemetry, shared by the analytics page and
// by the workers card on Settings. One copy, so the two screens cannot drift
// into rendering the same worker as "99.9%" on one and "100%" on the other.

export interface WorkerDaySeries {
  day: string;
  checks: number;
  availabilityPercent: number;
  avgDurationMs: number;
  avgDistanceKm: number;
  disconnects: number;
}

export interface WorkerCountryShare {
  country: string;
  checks: number;
  percent: number;
}

export interface WorkerOutage {
  from: string;
  to?: string;
  seconds?: number;
}

export interface WorkerTelemetry {
  workerId: string;
  workerName: string;
  checks: number;
  checksIp: number;
  checksProxy: number;
  sharePercent: number;
  availabilityPercent: number;
  disconnects: number;
  longestOutageSec: number;
  totalOutageSec: number;
  avgDurationMs: number;
  maxDurationMs: number;
  avgDurationMsIp: number;
  avgDurationMsProxy: number;
  measuredChecks: number;
  p50DurationMs: number;
  p95DurationMs: number;
  avgWaitMs: number;
  maxWaitMs: number;
  avgProbeMs: number;
  // Time a proxy check spent on targets that did not answer. A property of the
  // target, not of this worker — see the tooltip in the Check time column.
  avgLostMs: number;
  utilisationPercent: number;
  saturatedPercent: number;
  avgQueue: number;
  maxQueue: number;
  secondOpinions: number;
  agreementPercent: number;
  onlyThisWorkerFail: number;
  onlyOtherFail: number;
  avgDistanceKm: number;
  maxDistanceKm: number;
  geoOptimalPercent: number;
  geoEvaluatedChecks: number;
  countryKnownChecks: number;
  states?: Record<string, number>;
  countries?: WorkerCountryShare[];
  days: WorkerDaySeries[];
  outages?: WorkerOutage[];
}

export const formatDuration = (sec?: number): string => {
  if (!sec || sec <= 0) return '—';
  // Rounded to the sampling grid: outage boundaries are sample instants, so a
  // figure like "47s" would claim a resolution the measurement does not have.
  if (sec < 90) return `~${Math.max(30, Math.round(sec / 30) * 30)}s`;
  if (sec < 5400) return `${Math.round(sec / 60)}m`;
  if (sec < 172800) return `${(sec / 3600).toFixed(1)}h`;
  return `${(sec / 86400).toFixed(1)}d`;
};

// Check durations run from a few hundred ms to tens of seconds. "16380 ms" is
// five digits of precision nobody reads and the widest cell in the row; "16.4 s"
// is the same fact in half the width. Exact milliseconds stay in the tooltips.
//
// This is the ONE duration formatter for the live-operations screens (check
// logs, diagnostics, worker analytics) so the same measurement never reads as
// "1240 ms" on one screen and "1.2 s" on the next. Sub-10 ms keeps a decimal:
// an ICMP round trip of 0.4 ms is a real number, and rounding it to "0 ms"
// reads as a missing value.
export const formatMs = (ms?: number): string => {
  if (!ms || ms <= 0) return '—';
  if (ms < 10) return `${ms.toFixed(1)} ms`;
  if (ms < 1000) return `${Math.round(ms)} ms`;
  if (ms < 60000) return `${(ms / 1000).toFixed(1)} s`;
  return `${(ms / 60000).toFixed(1)} m`;
};

/** Measurements are read down a column, so their digits must line up. */
export const NUM_STYLE: React.CSSProperties = { fontVariantNumeric: 'tabular-nums' };

export const formatKm = (km?: number): string =>
  km && km > 0 ? `${Math.round(km).toLocaleString()} km` : '—';

// Availability is reported as -1 when nothing was sampled. That is not 0%, and
// showing it as 0% would put a red number against a worker nobody watched.
// marginInlineEnd 0: antd gives every Tag an 8px trailing margin, which floats
// the tag off the right edge of a right-aligned measurement column.
export const availabilityTag = (pct: number): React.ReactElement => {
  if (pct < 0) return <Tag style={{ marginInlineEnd: 0 }}>not sampled</Tag>;
  const color = pct >= 99.5 ? 'success' : pct >= 95 ? 'warning' : 'error';
  return <Tag color={color} style={{ marginInlineEnd: 0, ...NUM_STYLE }}>{pct.toFixed(1)}%</Tag>;
};

// Availability comes from a sample every 30 seconds, so a drop shorter than
// that leaves no trace and every recorded outage is a whole number of ticks.
// Saying so is the difference between "this worker was up all day" and "we
// looked 2,880 times and it answered every time".
export const SAMPLE_NOTE = 'connection sampled every 30s — shorter drops are not visible';
